import { useEffect, useRef } from "react";

const features = [
  {
    icon: "ri-store-2-line",
    title: "Live Lead Marketplace",
    description: "Browse available leads in real-time with advanced filters by state, price range, and lead score. Reserve instantly.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Exclusive Leads",
    description: "1 lead = 1 installer. Never shared, never resold. Once you purchase, the lead is yours exclusively.",
  },
  {
    icon: "ri-user-star-line",
    title: "Dual Scoring System",
    description: "Every lead has a Lead Score (quality) and Solar Score (potential). Filter by 50-99 to match your criteria.",
  },
  {
    icon: "ri-shopping-bag-line",
    title: "Lead Management",
    description: "Track purchased leads through your pipeline: New → Contacted → Qualified → Appointment → Closed. Add notes and updates.",
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Geo-Targeted Settings",
    description: "Set your service states and target ZIP codes in your dashboard. Only see leads where you actually work.",
  },
  {
    icon: "ri-bank-card-line",
    title: "Transparent Billing",
    description: "View complete transaction history, total spent, and average cost per lead. Secure Stripe checkout on every purchase.",
  },
];

export default function WhatYouGet() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll(".feature-card");
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = "1";
                (card as HTMLElement).style.transform = "translateY(0)";
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="what-you-get" className="py-24 md:py-32 bg-[#0A1F0F]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-green-400 text-xs font-bold uppercase tracking-widest mb-3 block">Value Stack</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">What You Get</h2>
          <div className="w-16 h-1 bg-green-500 rounded-full mx-auto mt-4"></div>
          <p className="mt-5 text-gray-400 text-lg max-w-xl mx-auto">
            Everything you need to close more solar deals — without the guesswork.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="feature-card bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/10 hover:border-green-500/30 transition-all duration-300 cursor-default group"
              style={{ opacity: 0, transform: "translateY(24px)", transition: "opacity 0.5s ease, transform 0.5s ease" }}
            >
              <div className="w-11 h-11 flex items-center justify-center bg-green-500/10 rounded-xl mb-4 group-hover:bg-green-500/20 transition-colors">
                <i className={`${feature.icon} text-xl text-green-400`}></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
